// visualEvolution.ts: Complete visual evolution system for nodes and connections
// Implements stage-based geometric morphology, effects, and animations

import { Node, Edge } from './types';

export type EvolutionStage = 
    | 'embryonic'
    | 'primitive'
    | 'specialized'
    | 'integrated'
    | 'transcendent'
    | 'mystical'
    | 'divine'
    | 'cosmic'
    | 'infinite';

export type NodeShapeType = 
    | 'circle'
    | 'polygon'
    | 'star'
    | 'compound'
    | 'sacred'
    | 'fluid'
    | 'light'
    | 'galaxy'
    | 'impossible';

export interface VisualProperties {
    stage: EvolutionStage;
    energy: number;
    wisdom: number;
    love: number;
    beauty: number;
    truth: number;
    transcendence: number;
    nodeType?: string;
}

export interface NodeVisualState {
    shape: NodeShapeType;
    size: number;
    opacity: number;
    glowIntensity: number;
    glowRadius: number;
    pulsePhase: number;
    rotationAngle: number;
    morphProgress: number;
    color: string;
    secondaryColor?: string;
    borderWidth: number;
    borderColor: string;
    particleEmissionRate: number;
}

export interface ConnectionVisualState {
    thickness: number;
    opacity: number;
    color: string;
    flowSpeed: number;
    particleCount: number;
    waveAmplitude: number;
    waveFrequency: number;
    glowIntensity: number;
    curveAmount: number;
}

export interface RenderParticle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    maxLife: number;
    size: number;
    color: string;
    type: 'energy' | 'wisdom' | 'love' | 'creation' | 'cosmic';
    opacity: number;
}

const GOLDEN_RATIO = 1.618033988749895;
const FIBONACCI = [1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144];

export class VisualEvolutionEngine {
    private particles: RenderParticle[] = [];
    private time: number = 0;
    private maxParticles: number = 500;
    
    // Performance optimizations
    private visualStateCache: Map<number, { state: NodeVisualState, timestamp: number }> = new Map();
    private cacheDuration: number = 0.1; // Cache for 100ms
    private lastFrameDuration: number = 16; // Track frame time for LOD
    
    // ── Object pool for NodeVisualState (eliminates per-frame allocations) ──
    private _visualStatePool: NodeVisualState[] = [];
    private readonly _maxPoolSize = 64;
    
    /** Acquire a NodeVisualState from the pool (or create new) */
    private _acquireState(): NodeVisualState {
        return this._visualStatePool.pop() || {
            shape: 'circle', size: 8, opacity: 0.65, glowIntensity: 0, glowRadius: 0,
            pulsePhase: 0, rotationAngle: 0, morphProgress: 0, color: '#fff',
            borderWidth: 0, borderColor: 'transparent', particleEmissionRate: 0
        };
    }
    
    /** Release a NodeVisualState back to the pool */
    private _releaseState(state: NodeVisualState): void {
        if (this._visualStatePool.length < this._maxPoolSize) {
            this._visualStatePool.push(state);
        }
    }
    // ── 5-Tier LOD (expanded from 3-level): 0=full, 1=high, 2=medium, 3=low, 4=extreme ──
    private lodLevel: number = 0;
    /** External LOD override from QualityTierManager. When set, disables internal auto-LOD. */
    private externalLodLevel: number = -1;
    /** Whether the external quality manager is active (disables internal auto-LOD) */
    private useExternalLOD: boolean = false;
    
    constructor() {
        this.time = Date.now() / 1000;
    }
    
    /** Enable external LOD control (from QualityTierManager). Disables internal auto-LOD. */
    enableExternalLOD(): void { this.useExternalLOD = true; }
    /** Disable external LOD, revert to internal auto-LOD. */
    disableExternalLOD(): void { this.useExternalLOD = false; }
    
    /** Set LOD level from external quality manager (0-4). */
    setExternalLODLevel(level: number): void {
        this.externalLodLevel = Math.max(0, Math.min(4, Math.round(level)));
        if (this.useExternalLOD) {
            this._applyLODLevel(this.externalLodLevel);
        }
    }
    
    /** Apply an LOD level with maxParticles settings. */
    private _applyLODLevel(level: number): void {
        this.lodLevel = level;
        switch (level) {
            case 0: this.maxParticles = 600; break;  // Full quality
            case 1: this.maxParticles = 450; break;  // High
            case 2: this.maxParticles = 300; break;  // Medium
            case 3: this.maxParticles = 150; break;  // Low
            case 4: this.maxParticles = 60;  break;  // Extreme
            default: this.maxParticles = 300; break;
        }
    }
    
    update(deltaTime: number): void {
        this.time += deltaTime;
        
        // Only run internal auto-LOD if external control is disabled
        if (!this.useExternalLOD) {
            // Track frame time for adaptive LOD
            const frameDuration = deltaTime * 1000;
            this.lastFrameDuration = this.lastFrameDuration * 0.9 + frameDuration * 0.1; // Smooth average
            
            // Adjust LOD based on performance (5-tier degradation with hysteresis)
            if (this.lastFrameDuration > 50) {          // Below 20 FPS
                this._applyLODLevel(4); // extreme
            } else if (this.lastFrameDuration > 33) {   // Below 30 FPS
                this._applyLODLevel(3); // low
            } else if (this.lastFrameDuration > 25) {   // Below 40 FPS
                this._applyLODLevel(2); // medium
            } else if (this.lastFrameDuration > 20) {   // Below 50 FPS
                this._applyLODLevel(1); // high
            } else if (this.lastFrameDuration < 12 && this.lodLevel > 0) {
                // Recovery: only recover one level at a time (requires sustained good perf)
                this._applyLODLevel(Math.max(0, this.lodLevel - 1));
            }
        }
        
        // Update particles
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];
            p.x += p.vx * deltaTime;
            p.y += p.vy * deltaTime;
            p.life -= deltaTime;
            p.opacity = Math.max(0, p.life / p.maxLife);
            
            if (p.life <= 0) {
                this.particles.splice(i, 1);
            }
        }
        
        // Cleanup cache entries older than cacheDuration — release to pool
        const now = this.time;
        for (const [key, value] of this.visualStateCache) {
            if (now - value.timestamp > this.cacheDuration) {
                this._releaseState(value.state);
                this.visualStateCache.delete(key);
            }
        }
        // ── Periodic full pool drain: every ~10s, reset pool to prevent memory creep ──
        if (Math.floor(this.time) % 10 === 0 && this._visualStatePool.length > this._maxPoolSize) {
            this._visualStatePool.length = this._maxPoolSize;
        }
    }
    
    // Get node visual state based on properties (with caching)
    getNodeVisualState(node: Node, props: VisualProperties): NodeVisualState {
        // Check cache first
        const cacheKey = node.id;
        const cached = this.visualStateCache.get(cacheKey);
        if (cached && (this.time - cached.timestamp) < this.cacheDuration) {
            return cached.state;
        }
        
        const stage = this.getNodeEvolutionStage(node, props);
        const baseSize = this.getNodeBaseSize(node, stage);
        const shape = this.getNodeShape(stage, props.nodeType);
        const pulseFactor = this.getPulseFactor(stage, props.energy);
        
        const state: NodeVisualState = this._acquireState();
        state.shape = shape;
        state.size = baseSize * pulseFactor;
        state.opacity = Math.max(this.getNodeOpacityFloor(), this.getNodeOpacity(stage, props.truth));
        state.glowIntensity = this.getGlowIntensity(props);
        state.glowRadius = this.getGlowRadius(stage, props.energy);
        state.pulsePhase = this.time * this.getPulseFrequency(stage);
        state.rotationAngle = this.time * this.getRotationSpeed(stage);
        state.morphProgress = (Math.sin(this.time * 0.3) + 1) / 2;
        state.color = this.getNodeColor(node.color, props);
        state.secondaryColor = this.getSecondaryColor(node.color, props);
        state.borderWidth = this.getBorderWidth(stage);
        state.borderColor = this.getBorderColor(node.color, stage);
        state.particleEmissionRate = this.getParticleEmissionRate(stage, props.energy) * this.getParticleEmissionMultiplier();
        
        // Cache the result
        this.visualStateCache.set(cacheKey, { state, timestamp: this.time });
        
        return state;
    }
    
    // Particle emission multiplier based on LOD (5-tier)
    private getParticleEmissionMultiplier(): number {
        // GOLDEN RULE: NEVER return 0 — always emit SOME particles
        switch (this.lodLevel) {
            case 0: return 1.0;   // Full
            case 1: return 0.75;  // High
            case 2: return 0.5;   // Medium
            case 3: return 0.25;  // Low
            case 4: return 0.08;  // Extreme — floor at 0.08 (never zero)
            default: return 1.0;
        }
    }
    
    // Check if node is in viewport (for culling)
    isNodeInViewport(x: number, y: number, size: number, canvasWidth: number, canvasHeight: number): boolean {
        const margin = size * 3; // Include glow radius
        return x + margin > 0 && x - margin < canvasWidth && y + margin > 0 && y - margin < canvasHeight;
    }
    
    // Check if connection should be rendered (distance culling with 5-tier LOD)
    shouldRenderConnection(x1: number, y1: number, x2: number, y2: number, stage: EvolutionStage): boolean {
        // At LOD 2+, skip rendering distant connections
        if (this.lodLevel >= 2) {
            const dx = x2 - x1;
            const dy = y2 - y1;
            const distance = Math.sqrt(dx * dx + dy * dy);
            // Progressively shorter max distance at higher LOD levels
            const maxDistances = [Infinity, Infinity, 800, 500, 250];
            const maxDistance = maxDistances[this.lodLevel] ?? 500;
            if (distance > maxDistance) return false;
        }
        return true;
    }
    
    // Check if glow should be rendered (selective rendering, 5-tier)
    shouldRenderGlow(glowIntensity: number): boolean {
        // GOLDEN RULE: NEVER return false for ALL glows — keep a floor
        if (this.lodLevel === 0) return true;
        if (this.lodLevel === 1) return glowIntensity > 0.2;
        if (this.lodLevel === 2) return glowIntensity > 0.35;
        if (this.lodLevel === 3) return glowIntensity > 0.55;
        // LOD 4 (extreme): only brightest glows visible, but never all hidden
        return glowIntensity > 0.75;
    }
    
    // Get current LOD level
    getLODLevel(): number {
        return this.lodLevel;
    }
    
    // Get connection visual state
    getConnectionVisualState(edge: Edge, fromNode: Node, toNode: Node, props: VisualProperties): ConnectionVisualState {
        const stage = this.getNodeEvolutionStage(fromNode, props);
        const weight = edge.weight || 0.5;
        
        return {
            thickness: this.getConnectionThickness(stage, weight),
            opacity: this.getConnectionOpacity(stage, weight),
            color: this.getConnectionColor(fromNode.color, toNode.color, stage),
            flowSpeed: this.getFlowSpeed(stage, props.energy),
            particleCount: this.getConnectionParticleCount(stage, weight),
            waveAmplitude: this.getWaveAmplitude(stage),
            waveFrequency: this.getWaveFrequency(stage),
            glowIntensity: this.getConnectionGlowIntensity(stage, weight),
            curveAmount: this.getCurveAmount(stage)
        };
    }
    
    // Node shape generation
    private getNodeShape(stage: EvolutionStage, nodeType?: string): NodeShapeType {
        switch (stage) {
            case 'embryonic': return 'circle';
            case 'primitive': return 'circle';
            case 'specialized': return 'polygon';
            case 'integrated': return 'compound';
            case 'transcendent': return 'sacred';
            case 'mystical': return 'fluid';
            case 'divine': return 'light';
            case 'cosmic': return 'galaxy';
            case 'infinite': return 'impossible';
            default: return 'circle';
        }
    }
    
    // Determine evolution stage from node properties
    private getNodeEvolutionStage(node: Node, props: VisualProperties): EvolutionStage {
        // Use provided stage if available, otherwise calculate from properties
        if (props.stage) return props.stage;
        
        const complexity = (props.energy + props.wisdom + props.transcendence) / 3;
        
        if (complexity < 0.1) return 'embryonic';
        if (complexity < 0.2) return 'primitive';
        if (complexity < 0.4) return 'specialized';
        if (complexity < 0.6) return 'integrated';
        if (complexity < 0.75) return 'transcendent';
        if (complexity < 0.85) return 'mystical';
        if (complexity < 0.95) return 'divine';
        if (complexity < 0.99) return 'cosmic';
        return 'infinite';
    }
    
    // Base size progression by stage
    private getNodeBaseSize(node: Node, stage: EvolutionStage): number {
        const sizeMap: Record<EvolutionStage, number> = {
            embryonic: 8,
            primitive: 10,
            specialized: 12,
            integrated: 14,
            transcendent: 16,
            mystical: 19,
            divine: 22,
            cosmic: 25,
            infinite: 28
        };
        return sizeMap[stage];
    }
    
    // Opacity progression by stage and truth level
    private getNodeOpacity(stage: EvolutionStage, truth: number): number {
        const baseOpacity: Record<EvolutionStage, number> = {
            embryonic: 0.65,
            primitive: 0.75,
            specialized: 0.82,
            integrated: 0.87,
            transcendent: 0.92,
            mystical: 0.95,
            divine: 0.98,
            cosmic: 0.96,
            infinite: 1.0
        };
        
        // Truth increases transparency
        const truthModifier = truth * 0.15;
        return Math.max(0.4, Math.min(1.0, baseOpacity[stage] - truthModifier));
    }
    
    // Calculate glow intensity from properties
    private getGlowIntensity(props: VisualProperties): number {
        const baseGlow = props.energy * 0.3;
        const wisdomGlow = props.wisdom * 0.25;
        const transcendenceGlow = props.transcendence * 0.3;
        const beautyGlow = props.beauty * 0.15;
        
        return Math.min(1.0, baseGlow + wisdomGlow + transcendenceGlow + beautyGlow);
    }
    
    // Glow radius calculation
    private getGlowRadius(stage: EvolutionStage, energy: number): number {
        const stageMultiplier: Record<EvolutionStage, number> = {
            embryonic: 1.2,
            primitive: 1.4,
            specialized: 1.6,
            integrated: 1.9,
            transcendent: 2.3,
            mystical: 2.8,
            divine: 3.5,
            cosmic: 4.2,
            infinite: 5.0
        };
        
        return stageMultiplier[stage] * (1 + energy * 0.5);
    }
    
    // Pulse factor for breathing animation
    private getPulseFactor(stage: EvolutionStage, energy: number): number {
        const amplitude = this.getPulseAmplitude(stage);
        const frequency = this.getPulseFrequency(stage);
        return 1 + amplitude * Math.sin(this.time * frequency * Math.PI * 2);
    }
    
    private getPulseAmplitude(stage: EvolutionStage): number {
        const amplitudes: Record<EvolutionStage, number> = {
            embryonic: 0.08,
            primitive: 0.12,
            specialized: 0.10,
            integrated: 0.08,
            transcendent: 0.15,
            mystical: 0.20,
            divine: 0.18,
            cosmic: 0.12,
            infinite: 0.25
        };
        return amplitudes[stage];
    }
    
    private getPulseFrequency(stage: EvolutionStage): number {
        const frequencies: Record<EvolutionStage, number> = {
            embryonic: 0.8,
            primitive: 1.0,
            specialized: 1.2,
            integrated: 1.0,
            transcendent: 0.9,
            mystical: 0.6,
            divine: 0.5,
            cosmic: 0.3,
            infinite: 0.4
        };
        return frequencies[stage];
    }
    
    private getRotationSpeed(stage: EvolutionStage): number {
        const speeds: Record<EvolutionStage, number> = {
            embryonic: 0,
            primitive: 0,
            specialized: 0.05,
            integrated: 0.08,
            transcendent: 0.12,
            mystical: 0.18,
            divine: 0.15,
            cosmic: 0.10,
            infinite: 0.25
        };
        return speeds[stage];
    }
    
    // Color evolution with property overlays
    private getNodeColor(baseColor: string, props: VisualProperties): string {
        const hueMatch = baseColor.match(/hsl\((\d+)/);
        let hue = hueMatch ? parseInt(hueMatch[1]) : 220;
        
        // Wisdom adds gold
        if (props.wisdom > 0.5) {
            hue = hue + (45 - hue) * (props.wisdom - 0.5) * 0.4;
        }
        
        // Love adds warmth (toward pink/rose)
        if (props.love > 0.5) {
            const loveInfluence = (props.love - 0.5) * 2;
            hue = hue + (330 - hue) * loveInfluence * 0.3;
        }
        
        // Transcendence increases brightness
        const saturation = 70 + props.transcendence * 25;
        const lightness = 55 + props.transcendence * 30 + props.beauty * 15;
        
        return `hsl(${Math.round(hue)}, ${Math.round(saturation)}%, ${Math.round(lightness)}%)`;
    }
    
    private getSecondaryColor(baseColor: string, props: VisualProperties): string | undefined {
        if (props.transcendence < 0.4) return undefined;
        
        const hueMatch = baseColor.match(/hsl\((\d+)/);
        let hue = hueMatch ? parseInt(hueMatch[1]) : 220;
        
        // Complementary or triadic color
        const offset = props.beauty > 0.6 ? 120 : 180; // Triadic or complementary
        const secondaryHue = (hue + offset) % 360;
        
        return `hsl(${secondaryHue}, 75%, 65%)`;
    }
    
    private getBorderWidth(stage: EvolutionStage): number {
        const widths: Record<EvolutionStage, number> = {
            embryonic: 0.5,
            primitive: 1.0,
            specialized: 1.5,
            integrated: 2.0,
            transcendent: 2.5,
            mystical: 2.0,
            divine: 0,
            cosmic: 3.0,
            infinite: 0
        };
        return widths[stage];
    }
    
    private getBorderColor(baseColor: string, stage: EvolutionStage): string {
        if (stage === 'divine' || stage === 'infinite') return 'transparent';
        
        const hueMatch = baseColor.match(/hsl\((\d+)/);
        const hue = hueMatch ? parseInt(hueMatch[1]) : 220;
        
        return `hsl(${hue}, 60%, 30%)`;
    }
    
    private getParticleEmissionRate(stage: EvolutionStage, energy: number): number {
        const rates: Record<EvolutionStage, number> = {
            embryonic: 0,
            primitive: 0.5,
            specialized: 1.0,
            integrated: 1.5,
            transcendent: 3.0,
            mystical: 5.0,
            divine: 8.0,
            cosmic: 12.0,
            infinite: 20.0
        };
        return rates[stage] * (0.5 + energy * 0.5);
    }
    
    // Connection visual properties
    private getConnectionThickness(stage: EvolutionStage, weight: number): number {
        const baseThickness: Record<EvolutionStage, number> = {
            embryonic: 1.0,
            primitive: 1.5,
            specialized: 2.0,
            integrated: 2.5,
            transcendent: 3.0,
            mystical: 3.5,
            divine: 4.5,
            cosmic: 5.5,
            infinite: 6.5
        };
        
        return baseThickness[stage] * (0.5 + weight * 0.5);
    }
    
    private getConnectionOpacity(stage: EvolutionStage, weight: number): number {
        const baseOpacity: Record<EvolutionStage, number> = {
            embryonic: 0.4,
            primitive: 0.5,
            specialized: 0.65,
            integrated: 0.75,
            transcendent: 0.85,
            mystical: 0.90,
            divine: 0.95,
            cosmic: 0.92,
            infinite: 1.0
        };
        
        return baseOpacity[stage] * (0.6 + weight * 0.4);
    }
    
    private getConnectionColor(fromColor: string, toColor: string, stage: EvolutionStage): string {
        const fromHue = parseInt(fromColor.match(/hsl\((\d+)/)?.[1] || '220');
        const toHue = parseInt(toColor.match(/hsl\((\d+)/)?.[1] || '220');
        
        const avgHue = Math.round((fromHue + toHue) / 2);
        
        // Advanced stages have more luminous connections
        const saturation = 70 + Math.min(30, this.getStageIndex(stage) * 3);
        const lightness = 55 + Math.min(35, this.getStageIndex(stage) * 4);
        
        return `hsl(${avgHue}, ${saturation}%, ${lightness}%)`;
    }
    
    private getFlowSpeed(stage: EvolutionStage, energy: number): number {
        const baseSpeeds: Record<EvolutionStage, number> = {
            embryonic: 0.5,
            primitive: 0.8,
            specialized: 1.2,
            integrated: 1.5,
            transcendent: 2.0,
            mystical: 3.0,
            divine: 5.0,
            cosmic: 2.0,
            infinite: 10.0
        };
        
        return baseSpeeds[stage] * (0.5 + energy * 0.5);
    }
    
    private getConnectionParticleCount(stage: EvolutionStage, weight: number): number {
        const baseCounts: Record<EvolutionStage, number> = {
            embryonic: 1,
            primitive: 2,
            specialized: 3,
            integrated: 4,
            transcendent: 6,
            mystical: 8,
            divine: 12,
            cosmic: 15,
            infinite: 20
        };
        
        return Math.ceil(baseCounts[stage] * weight);
    }
    
    private getWaveAmplitude(stage: EvolutionStage): number {
        const amplitudes: Record<EvolutionStage, number> = {
            embryonic: 0,
            primitive: 1,
            specialized: 2,
            integrated: 2,
            transcendent: 3,
            mystical: 5,
            divine: 4,
            cosmic: 6,
            infinite: 8
        };
        return amplitudes[stage];
    }
    
    private getWaveFrequency(stage: EvolutionStage): number {
        return 0.5 + this.getStageIndex(stage) * 0.1;
    }
    
    private getConnectionGlowIntensity(stage: EvolutionStage, weight: number): number {
        return (this.getStageIndex(stage) / 8) * weight;
    }
    
    private getCurveAmount(stage: EvolutionStage): number {
        // Transcendent and higher stages have curved connections
        if (this.getStageIndex(stage) < 4) return 0;
        return (this.getStageIndex(stage) - 3) * 0.15;
    }
    
    private getStageIndex(stage: EvolutionStage): number {
        const stages: EvolutionStage[] = [
            'embryonic', 'primitive', 'specialized', 'integrated',
            'transcendent', 'mystical', 'divine', 'cosmic', 'infinite'
        ];
        return stages.indexOf(stage);
    }
    
    // === PARTICLE SYSTEM ===
    
    emitParticle(x: number, y: number, props: VisualProperties): void {
        if (this.particles.length >= this.maxParticles) return;
        
        const type = this.getParticleType(props);
        const angle = Math.random() * Math.PI * 2;
        const speed = 10 + Math.random() * 30;
        
        this.particles.push({
            x,
            y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            life: 0.5 + Math.random() * 1.5,
            maxLife: 2.0,
            size: 2 + Math.random() * 3,
            color: this.getParticleColor(type, props),
            type,
            opacity: 1.0
        });
    }
    
    private getParticleType(props: VisualProperties): RenderParticle['type'] {
        if (props.transcendence > 0.7) return 'cosmic';
        if (props.wisdom > 0.6) return 'wisdom';
        if (props.love > 0.6) return 'love';
        if (props.energy > 0.7) return 'creation';
        return 'energy';
    }
    
    private getParticleColor(type: RenderParticle['type'], props: VisualProperties): string {
        switch (type) {
            case 'energy': return `hsla(60, 100%, 60%, 0.8)`;
            case 'wisdom': return `hsla(45, 90%, 65%, 0.9)`;
            case 'love': return `hsla(330, 85%, 70%, 0.85)`;
            case 'creation': return `hsla(280, 90%, 75%, 0.9)`;
            case 'cosmic': return `hsla(220, 95%, 80%, 0.95)`;
        }
    }
    
    getParticles(): RenderParticle[] {
        return this.particles;
    }
    
    // === SHAPE RENDERING UTILITIES ===
    
    renderNodeShape(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        visualState: NodeVisualState,
        props: VisualProperties
    ): void {
        const stage = this.getNodeEvolutionStage({} as Node, props);
        
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(visualState.rotationAngle);
        
        // OPTIMIZATION: Simplify shapes at lower LOD
        const simplifiedShape = this.getSimplifiedShape(visualState.shape);
        
        switch (simplifiedShape) {
            case 'circle':
                this.renderCircle(ctx, visualState);
                break;
            case 'polygon':
                this.renderPolygon(ctx, visualState, props);
                break;
            case 'compound':
                this.renderCompound(ctx, visualState, props);
                break;
            case 'sacred':
                this.renderSacredGeometry(ctx, visualState, props);
                break;
            case 'fluid':
                this.renderFluid(ctx, visualState, props);
                break;
            case 'light':
                this.renderLight(ctx, visualState, props);
                break;
            case 'galaxy':
                this.renderGalaxy(ctx, visualState, props);
                break;
            case 'impossible':
                this.renderImpossible(ctx, visualState, props);
                break;
            case 'star':
                this.renderStar(ctx, visualState, props);
                break;
        }
        
        ctx.restore();
    }
    
    // Simplify complex shapes at lower LOD (5-tier)
    // GOLDEN RULE: NEVER simplify to invisible — always keep a renderable shape
    private getSimplifiedShape(shape: NodeShapeType): NodeShapeType {
        if (this.lodLevel === 0) return shape;  // Full quality: no simplification
        
        // LOD 1 (high): Minor simplifications for most complex shapes
        if (this.lodLevel === 1) {
            if (shape === 'impossible') return 'galaxy';
            if (shape === 'galaxy') return 'compound';
            return shape;
        }
        
        // LOD 2 (medium): Simplify complex shapes
        if (this.lodLevel === 2) {
            if (shape === 'impossible') return 'compound';
            if (shape === 'galaxy') return 'sacred';
            if (shape === 'fluid') return 'polygon';
            if (shape === 'light') return 'compound';
            return shape;
        }
        
        // LOD 3 (low): Heavy simplification
        if (this.lodLevel === 3) {
            if (shape === 'impossible' || shape === 'galaxy' || shape === 'compound' || shape === 'sacred') return 'polygon';
            if (shape === 'fluid' || shape === 'light') return 'star';
            return shape;
        }
        
        // LOD 4 (extreme): Everything becomes simple circle or star
        // NEVER return a shape that won't render — circle is the fallback
        if (shape === 'circle' || shape === 'star' || shape === 'polygon') return shape;
        return 'circle';
    }
    
    private renderCircle(ctx: CanvasRenderingContext2D, vs: NodeVisualState): void {
        ctx.beginPath();
        ctx.arc(0, 0, Math.max(0.1, vs.size), 0, Math.PI * 2);
        ctx.fillStyle = vs.color;
        ctx.globalAlpha = vs.opacity;
        ctx.fill();
        
        if (vs.borderWidth > 0) {
            ctx.strokeStyle = vs.borderColor;
            ctx.lineWidth = vs.borderWidth;
            ctx.stroke();
        }
    }
    
    private renderPolygon(ctx: CanvasRenderingContext2D, vs: NodeVisualState, props: VisualProperties): void {
        const sides = this.getPolygonSides(props.nodeType);
        const angleStep = (Math.PI * 2) / sides;
        
        ctx.beginPath();
        for (let i = 0; i <= sides; i++) {
            const angle = i * angleStep - Math.PI / 2;
            const radius = vs.size;
            const px = Math.cos(angle) * radius;
            const py = Math.sin(angle) * radius;
            
            if (i === 0) {
                ctx.moveTo(px, py);
            } else {
                ctx.lineTo(px, py);
            }
        }
        ctx.closePath();
        
        ctx.fillStyle = vs.color;
        ctx.globalAlpha = vs.opacity;
        ctx.fill();
        
        if (vs.borderWidth > 0) {
            ctx.strokeStyle = vs.borderColor;
            ctx.lineWidth = vs.borderWidth;
            ctx.stroke();
        }
    }
    
    private getPolygonSides(nodeType?: string): number {
        if (!nodeType) return 6;
        
        const typeMap: Record<string, number> = {
            vowel: 5,
            consonant: 6,
            syllable: 7,
            word: 8,
            semantic: 8,
            default: 6
        };
        
        return typeMap[nodeType] || typeMap.default;
    }
    
    private renderCompound(ctx: CanvasRenderingContext2D, vs: NodeVisualState, props: VisualProperties): void {
        // Outer polygon
        this.renderPolygon(ctx, vs, props);
        
        // Inner star
        const innerSize = vs.size * 0.6;
        const points = 8;
        const angleStep = (Math.PI * 2) / points;
        
        ctx.beginPath();
        for (let i = 0; i <= points * 2; i++) {
            const angle = (i * angleStep / 2) - Math.PI / 2;
            const radius = i % 2 === 0 ? innerSize : innerSize * 0.4;
            const px = Math.cos(angle) * radius;
            const py = Math.sin(angle) * radius;
            
            if (i === 0) {
                ctx.moveTo(px, py);
            } else {
                ctx.lineTo(px, py);
            }
        }
        ctx.closePath();
        
        if (vs.secondaryColor) {
            ctx.fillStyle = vs.secondaryColor;
            ctx.globalAlpha = vs.opacity * 0.7;
            ctx.fill();
        }
    }
    
    private renderSacredGeometry(ctx: CanvasRenderingContext2D, vs: NodeVisualState, props: VisualProperties): void {
        // Flower of Life inspired pattern
        const petals = 6;
        const petalRadius = vs.size * 0.7;
        
        // Central circle
        this.renderCircle(ctx, vs);
        
        // Surrounding petals
        for (let i = 0; i < petals; i++) {
            const angle = (i * Math.PI * 2) / petals;
            const px = Math.cos(angle) * petalRadius;
            const py = Math.sin(angle) * petalRadius;
            
            ctx.beginPath();
            ctx.arc(px, py, Math.max(0.1, vs.size * 0.5), 0, Math.PI * 2);
            ctx.strokeStyle = vs.secondaryColor || vs.color;
            ctx.lineWidth = 1;
            ctx.globalAlpha = vs.opacity * 0.5;
            ctx.stroke();
        }
    }
    
    private renderFluid(ctx: CanvasRenderingContext2D, vs: NodeVisualState, props: VisualProperties): void {
        // Morphing blob using perlin-like noise
        const points = 12;
        const angleStep = (Math.PI * 2) / points;
        
        ctx.beginPath();
        for (let i = 0; i <= points; i++) {
            const angle = i * angleStep;
            const noise = Math.sin(angle * 3 + this.time * 2) * 0.2 + 
                         Math.cos(angle * 5 - this.time * 1.5) * 0.15;
            const radius = vs.size * (1 + noise);
            const px = Math.cos(angle) * radius;
            const py = Math.sin(angle) * radius;
            
            if (i === 0) {
                ctx.moveTo(px, py);
            } else {
                ctx.lineTo(px, py);
            }
        }
        ctx.closePath();
        
        // Gradient fill
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, Math.max(0.1, vs.size));
        gradient.addColorStop(0, vs.color);
        gradient.addColorStop(1, vs.secondaryColor || vs.color);
        
        ctx.fillStyle = gradient;
        ctx.globalAlpha = vs.opacity;
        ctx.fill();
    }
    
    private renderLight(ctx: CanvasRenderingContext2D, vs: NodeVisualState, props: VisualProperties): void {
        // Pure light - multiple layers of glow
        const layers = 5;
        
        for (let i = layers; i > 0; i--) {
            const layerSize = vs.size * (i / layers) * 1.5;
            const layerOpacity = (vs.opacity / layers) * (layers - i + 1) * 0.3;
            
            const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, layerSize);
            gradient.addColorStop(0, `rgba(255, 255, 255, ${layerOpacity})`);
            gradient.addColorStop(0.5, vs.color.replace('hsl', 'hsla').replace(')', `, ${layerOpacity})`));
            gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
            
            ctx.fillStyle = gradient;
            ctx.globalAlpha = 1;
            ctx.beginPath();
            ctx.arc(0, 0, Math.max(0.1, layerSize), 0, Math.PI * 2);
            ctx.fill();
        }
    }
    
    private renderGalaxy(ctx: CanvasRenderingContext2D, vs: NodeVisualState, props: VisualProperties): void {
        // Galaxy spiral
        const arms = 3;
        const points = 50;
        
        for (let arm = 0; arm < arms; arm++) {
            const armAngle = (arm * Math.PI * 2) / arms;
            
            ctx.beginPath();
            for (let i = 0; i < points; i++) {
                const t = i / points;
                const angle = armAngle + t * Math.PI * 4;
                const radius = vs.size * t * 1.5;
                const px = Math.cos(angle) * radius;
                const py = Math.sin(angle) * radius;
                
                const dotSize = (1 - t) * 2 + 0.5;
                ctx.fillStyle = vs.color;
                ctx.globalAlpha = vs.opacity * (1 - t * 0.7);
                ctx.fillRect(px - dotSize/2, py - dotSize/2, dotSize, dotSize);
            }
        }
        
        // Core
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, vs.size * 0.3);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
        gradient.addColorStop(1, vs.color);
        
        ctx.beginPath();
        ctx.arc(0, 0, Math.max(0.1, vs.size * 0.3), 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.globalAlpha = vs.opacity;
        ctx.fill();
    }
    
    private renderImpossible(ctx: CanvasRenderingContext2D, vs: NodeVisualState, props: VisualProperties): void {
        // Penrose triangle inspired impossible geometry
        const size = vs.size;
        
        // Draw multiple overlapping shapes that create impossible geometry
        const shapes = 3;
        for (let i = 0; i < shapes; i++) {
            const angle = (i * Math.PI * 2) / shapes + this.time * 0.5;
            const offset = size * 0.3;
            
            ctx.save();
            ctx.rotate(angle);
            ctx.translate(offset, 0);
            
            // Triangle
            ctx.beginPath();
            for (let j = 0; j < 3; j++) {
                const a = (j * Math.PI * 2) / 3;
                const r = size * 0.5;
                const px = Math.cos(a) * r;
                const py = Math.sin(a) * r;
                if (j === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.closePath();
            
            ctx.fillStyle = vs.color;
            ctx.globalAlpha = vs.opacity * 0.6;
            ctx.fill();
            
            ctx.strokeStyle = vs.secondaryColor || vs.color;
            ctx.lineWidth = 2;
            ctx.stroke();
            
            ctx.restore();
        }
    }
    
    private renderStar(ctx: CanvasRenderingContext2D, vs: NodeVisualState, props: VisualProperties): void {
        const points = 8;
        const outerRadius = vs.size;
        const innerRadius = vs.size * 0.4;
        const angleStep = Math.PI / points;
        
        ctx.beginPath();
        for (let i = 0; i <= points * 2; i++) {
            const angle = i * angleStep - Math.PI / 2;
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const px = Math.cos(angle) * radius;
            const py = Math.sin(angle) * radius;
            
            if (i === 0) {
                ctx.moveTo(px, py);
            } else {
                ctx.lineTo(px, py);
            }
        }
        ctx.closePath();
        
        ctx.fillStyle = vs.color;
        ctx.globalAlpha = vs.opacity;
        ctx.fill();
        
        if (vs.borderWidth > 0) {
            ctx.strokeStyle = vs.borderColor;
            ctx.lineWidth = vs.borderWidth;
            ctx.stroke();
        }
    }
    
    // === GLOW RENDERING (LOD-aware) ===
    
    /** Get the glow opacity floor for current LOD level */
    getGlowOpacityFloor(): number {
        // GOLDEN RULE: NEVER make glow completely invisible
        const floors = [0.01, 0.01, 0.02, 0.03, 0.04];
        return floors[this.lodLevel] ?? 0.01;
    }
    
    /** Get the node opacity floor for current LOD level */
    getNodeOpacityFloor(): number {
        // GOLDEN RULE: NEVER make nodes completely invisible — always keep a dim render
        const floors = [0.0, 0.0, 0.08, 0.12, 0.15];
        return floors[this.lodLevel] ?? 0.0;
    }
    
    renderNodeGlow(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        visualState: NodeVisualState
    ): void {
        // Apply LOD floor — never fully hide
        const floorOpacity = this.getGlowOpacityFloor();
        const effectiveIntensity = Math.max(floorOpacity, visualState.glowIntensity);
        if (effectiveIntensity < 0.005) return;
        
        // LOD-based glow radius reduction
        const lodRadiusMultipliers = [1.0, 0.85, 0.7, 0.5, 0.35];
        const lodMultiplier = lodRadiusMultipliers[this.lodLevel] ?? 1.0;
        const glowRadius = visualState.size * visualState.glowRadius * lodMultiplier;
        
        const gradient = ctx.createRadialGradient(x, y, visualState.size * 0.5, x, y, Math.max(1, glowRadius));
        
        const glowColor = visualState.color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
        if (glowColor) {
            const [_, h, s, l] = glowColor;
            const glowAlpha = effectiveIntensity * visualState.opacity * 0.6;
            gradient.addColorStop(0, `hsla(${h}, ${s}%, ${Math.min(90, parseInt(l) + 20)}%, ${glowAlpha})`);
            gradient.addColorStop(0.5, `hsla(${h}, ${s}%, ${l}%, ${glowAlpha * 0.4})`);
            gradient.addColorStop(1, `hsla(${h}, ${s}%, ${l}%, 0)`);
        }
        
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, Math.max(0.1, glowRadius), 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
    
    // === CONNECTION RENDERING (LOD-aware) ===
    
    renderConnection(
        ctx: CanvasRenderingContext2D,
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        visualState: ConnectionVisualState,
        progress: number = 0.5
    ): void {
        // GOLDEN RULE: Apply LOD opacity floor — never fully hide connections
        const lodOpacityFloors = [0.0, 0.0, 0.06, 0.10, 0.12];
        const opacityFloor = lodOpacityFloors[this.lodLevel] ?? 0.0;
        const effectiveOpacity = Math.max(opacityFloor, visualState.opacity);
        
        ctx.save();
        
        // Draw connection line
        ctx.beginPath();
        
        // At extreme LOD (4), skip curves entirely for perf
        if (visualState.curveAmount > 0 && this.lodLevel < 4) {
            // Curved path
            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;
            const dx = x2 - x1;
            const dy = y2 - y1;
            
            // Control point perpendicular to line
            const cpX = midX - dy * visualState.curveAmount;
            const cpY = midY + dx * visualState.curveAmount;
            
            ctx.moveTo(x1, y1);
            ctx.quadraticCurveTo(cpX, cpY, x2, y2);
        } else {
            // Straight line
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
        }
        
        ctx.strokeStyle = visualState.color;
        // LOD-based line width reduction
        const lodWidthMultipliers = [1.0, 0.9, 0.75, 0.6, 0.5];
        ctx.lineWidth = Math.max(0.5, visualState.thickness * (lodWidthMultipliers[this.lodLevel] ?? 1.0));
        ctx.globalAlpha = effectiveOpacity;
        
        // Glow effect — skip at extreme LOD to save fill-rate
        if (visualState.glowIntensity > 0 && this.lodLevel < 3) {
            ctx.shadowColor = visualState.color;
            ctx.shadowBlur = visualState.thickness * visualState.glowIntensity * 3;
        }
        
        ctx.stroke();
        
        // Energy pulse — skip at LOD 3+ to save draw calls
        if (this.lodLevel < 3) {
            const pulseX = x1 + (x2 - x1) * progress;
            const pulseY = y1 + (y2 - y1) * progress;
            
            ctx.beginPath();
            ctx.arc(pulseX, pulseY, Math.max(0.1, visualState.thickness * 1.5), 0, Math.PI * 2);
            ctx.fillStyle = visualState.color;
            ctx.globalAlpha = effectiveOpacity * 0.8;
            if (visualState.glowIntensity > 0 && this.lodLevel < 3) {
                ctx.shadowBlur = visualState.thickness * 4;
            }
            ctx.fill();
        }
        
        ctx.restore();
    }
}
